import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { SphereLabel } from "@components/common/sphere-label";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Dialog, Divider, Text, useToast } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { ConnectDialogAutodeskHint } from "@pages/project-details/project-integrations/connect-dialog-autodesk-hint";
import { ConnectDialogProcoreHint } from "@pages/project-details/project-integrations/connect-dialog-procore-hint";
import { IntegrationAutocomplete } from "@pages/project-details/project-integrations/integration-autocomplete";
import { REAUTHORIZATION_NOT_COMPLETED } from "@services/integrations-service/integrations-service-constants";
import { PROCORE_ERROR_MESSAGES } from "@services/integrations-service/procore/procore-constants";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { memo, useEffect, useState } from "react";

/** Display name for the projects container of an integration */
const PROJECT_CONTAINER_LABEL = {
  autodesk: "hub",
  procore: "company"
};

/** Text to display when there are no project containers */
const NO_CONTAINERS_LABEL = {
  autodesk: "No hubs",
  procore: "No companies"
};
/** Renders project integration connect dialog */
function ProjectIntegrationsConnectDialogComponent({
  integrationsService,
  integrationId,
  setProjectIntegrations,
  setDialogState
}) {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const [isFetchingCompanies, setIsFetchingCompanies] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isFetchingProjects, setIsFetchingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcoreAppError, setIsProcoreAppError] = useState(false);
  const shouldShowProcoreAppHint = integrationId === "procore" && isProcoreAppError;
  const shouldShowAutodeskAppHint = integrationId === "autodesk";
  async function onConnectConfirm(selectedIntegrationProject) {
    trackEvent({
      name: ProjectEvents.connectIntegration,
      props: {
        integrationId
      }
    });
    try {
      setIsLoading(true);
      const updatedProjectIntegrations = await integrationsService.connectProject(selectedIntegrationProject);
      setProjectIntegrations(updatedProjectIntegrations);
      setDialogState("connected");
    } catch (error) {
      handleErrorWithToast({
        id: `connectProject-${Date.now().toString()}`,
        title: `Error connecting project to ${INTEGRATION_INFO[integrationId].displayName}`,
        error
      });
    } finally {
      setIsLoading(false);
    }
  }

  // Fetch companies of integration provider
  useEffect(() => {
    async function fetchCompanies() {
      setIsFetchingCompanies(true);
      try {
        const companies_0 = await integrationsService.getIntegrationCompanies(integrationId);
        setCompanies(companies_0);
      } catch (error_0) {
        // If the error is "reauthorization_not_completed" then just show a warning instead
        const message = getErrorDisplayMarkup(error_0);
        if (message === REAUTHORIZATION_NOT_COMPLETED) {
          openToast({
            title: "Unable to continue with the project connection.",
            message: "The integration reauthorization couldn't be completed.",
            variant: "warning"
          });
        } else {
          handleErrorWithToast({
            id: `fetchCompanies-${Date.now().toString()}`,
            title: "Error fetching integration workspaces",
            error: error_0
          });
        }
      }
      setIsFetchingCompanies(false);
    }
    void fetchCompanies();
  }, [handleErrorWithToast, integrationsService, integrationId, openToast]);

  // Fetch projects under selected company of integration provider
  useEffect(() => {
    async function fetchProjects() {
      if (!selectedCompany) {
        return;
      }
      setIsFetchingProjects(true);
      try {
        const projects_0 = await integrationsService.getIntegrationProjects(selectedCompany);
        setProjects(projects_0);
      } catch (error_1) {
        // If the error is "reauthorization_not_completed" then just show a warning instead
        const message_0 = getErrorDisplayMarkup(error_1);
        if (message_0 === REAUTHORIZATION_NOT_COMPLETED) {
          openToast({
            title: "Unable to continue with the project connection.",
            message: "The integration reauthorization couldn't be completed.",
            variant: "warning"
          });
        } else {
          handleErrorWithToast({
            id: `fetchProjects-${Date.now().toString()}`,
            title: "Error fetching integration projects",
            error: error_1
          });
        }

        // Extra error check for Procore. if the error message contains
        // "App is not connected to this company" is most probable that the
        // Sphere XG app is not installed in that Procore account.
        // Show a hint to the user in this case.
        if (message_0.includes(PROCORE_ERROR_MESSAGES.appNotConnected)) {
          setIsProcoreAppError(true);
        }
      }
      setIsFetchingProjects(false);
    }
    void fetchProjects();
  }, [handleErrorWithToast, integrationsService, selectedCompany, openToast]);
  function handleCompanyChange(value) {
    setSelectedCompany(value);
    setSelectedProject(null);
  }
  function handleProjectChange(value_0) {
    setSelectedProject(value_0);
  }
  async function handleOnConfirm() {
    if (selectedProject) {
      await onConnectConfirm(selectedProject);
    }
  }
  return <Dialog open={true} showSpinner={isLoading} isConfirmDisabled={!selectedCompany || !selectedProject} onConfirm={() => void handleOnConfirm()} confirmText="Enable" title={`Let's enable ${INTEGRATION_INFO[integrationId].displayName} in this project`} onCancel={() => setDialogState("none")}>
      <Stack>
        <Text variant="bodyM" sx={{
        marginBottom: SPACE_ELEMENTS_OF_MODAL
      }}>
          Select the <var>{INTEGRATION_INFO[integrationId].displayName}</var>{" "}
          project you would like to sync with this project.
        </Text>

        <Divider sx={{
        marginBottom: SPACE_ELEMENTS_OF_MODAL
      }} />

        {shouldShowProcoreAppHint && <ConnectDialogProcoreHint />}
        {shouldShowAutodeskAppHint && <ConnectDialogAutodeskHint />}

        {/* Workspace autocomplete */}
        <SphereLabel title={`${INTEGRATION_INFO[integrationId].displayName} ${PROJECT_CONTAINER_LABEL[integrationId]}`} />
        <IntegrationAutocomplete value={selectedCompany} options={companies} noOptionsText={NO_CONTAINERS_LABEL[integrationId]} onChange={handleCompanyChange} placeholder={`Select ${PROJECT_CONTAINER_LABEL[integrationId]}`} shouldFocus={true} shouldDisabled={isFetchingCompanies} />

        {/* Project autocomplete */}
        <SphereLabel title={<>
              <var style={{
          fontWeight: "inherit"
        }}>
                {INTEGRATION_INFO[integrationId].displayName}
              </var>{" "}
              project
            </>} sx={{
        marginTop: SPACE_ELEMENTS_OF_MODAL
      }} />
        <IntegrationAutocomplete value={selectedProject} options={projects} noOptionsText="No projects" placeholder="Select project" onChange={handleProjectChange} shouldDisabled={!selectedCompany || isFetchingProjects} />
      </Stack>
    </Dialog>;
}
export const ProjectIntegrationsConnectDialog = memo(ProjectIntegrationsConnectDialogComponent);